import React, { useState } from "react";
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Groups,
  TrendingUp,
  Security,
  Public,
  ExpandMore,
  HelpOutline,
  BusinessCenter,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import aboutbanner from "../Assests/Images/about.png";
import aboutdis from "../Assests/Images/aboutdis.png";

function About() {
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedFaq, setExpandedFaq] = useState(null); // Track which FAQ is expanded

  const faqs = [
    {
      q: "How do I reset my password?",
      a: 'Click on the "Forgot Password" link on the login page and follow the instructions sent to your email.',
    },
    {
      q: "How long does the interview process take?",
      a: "Coding tests usually take 45–60 minutes, while interviews last 30–45 minutes each.",
    },
    {
      q: "Can I apply for multiple jobs?",
      a: "Yes, you can apply for multiple jobs as long as you meet the requirements.",
    },
    {
      q: "Is ExiHire free for candidates?",
      a: "No, ExiHire is not free for candidates.",
    },
    {
      q: "Can companies customize interview rounds?",
      a: "Yes, companies can customize coding tests, technical interviews, and HR rounds.",
    },
    {
      q: "How secure is my data?",
      a: "We use industry-standard encryption and security practices to protect your data.",
    },
    {
      q: "Do you support remote interviews?",
      a: "Yes, ExiHire fully supports remote interviews with integrated tools.",
    },
    {
      q: "Which industries does ExiHire support?",
      a: "We support technology, finance, healthcare, education, and many other industries.",
    },
  ];

  const values = [
    {
      icon: <Groups fontSize="large" />,
      title: "Our Mission",
      description:
        "To simplify, automate, and streamline the hiring process for companies while providing a seamless and fair experience for candidates.",
    },
    {
      icon: <TrendingUp fontSize="large" />,
      title: "Our Vision",
      description:
        "To become the global standard for automated technical and HR assessments, delivering value and efficiency for both organizations and candidates.",
    },
    {
      icon: <Security fontSize="large" />,
      title: "Our Values",
      description:
        "Integrity, transparency, and fairness are at the heart of everything we build.",
    },
    {
      icon: <Public fontSize="large" />,
      title: "Global Reach",
      description:
        "Connecting top talent with the best opportunities across the world.",
    },
  ];

  const filteredFaqs = faqs.filter((faq) =>
    faq.q.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleAccordionChange = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  return (
    <Box sx={{ pt: 2, bgcolor: "#F8FAFC", backgroundImage: "radial-gradient(#E2E8F0 1px, transparent 1px)", backgroundSize: "20px 20px" }}>
      {/* Hero Section */}
      <Box
        sx={{
          backgroundImage: `url(${aboutbanner})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          height: { xs: "250px", sm: "400px", md: "500px" }, // adjust height as needed
          width: "100%",
          borderRadius: 0,
        }}
      />

      {/* Content Section */}
      <Container sx={{ py: { xs: 2, md: 4 } }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            {/* Badge */}
            <Box
              sx={{
                display: "inline-block",
                px: 2,
                py: 0.5,
                borderRadius: "20px",
                background: "linear-gradient(90deg, #6366F1 0%, #4F46E5 100%)",
                mb: 2,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: "white",
                  fontWeight: "bold",
                  textTransform: "uppercase",
                  letterSpacing: 1,
                }}
              >
                OUR PURPOSE
              </Typography>
            </Box>

            {/* Heading */}
            <Typography
              variant="h2"
              sx={{
                fontWeight: "bold",
                color: "#1E3A8A",
                fontSize: { xs: "2.5rem", md: "3.5rem" },
                mb: 1,
              }}
            >
              Our Purpose
            </Typography>

            {/* Gradient Underline */}
            <Box
              sx={{
                width: "80px",
                height: "4px",
                background: "linear-gradient(90deg, #4F46E5 0%, #9333EA 100%)",
                borderRadius: "2px",
                mb: 4,
              }}
            />

            {/* Purpose Items */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 3 }}>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    minWidth: 60,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #4F46E5 0%, #1E40AF 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 10px 15px -3px rgba(79, 70, 229, 0.4)",
                  }}
                >
                  <Groups sx={{ color: "white", fontSize: 30 }} />
                </Box>
                <Typography variant="body1" sx={{ color: "#374151", fontSize: "1.1rem", lineHeight: 1.6 }}>
                  ExiHire is a cutting-edge platform designed to revolutionize how
                  organizations conduct interviews. We provide a comprehensive suite
                  of tools that cover the entire recruitment lifecycle, from job
                  posting to offer letter generation.
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 3 }}>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    minWidth: 60,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #4F46E5 0%, #1E40AF 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 10px 15px -3px rgba(79, 70, 229, 0.4)",
                  }}
                >
                  <BusinessCenter sx={{ color: "white", fontSize: 30 }} />
                </Box>
                <Typography variant="body1" sx={{ color: "#374151", fontSize: "1.1rem", lineHeight: 1.6 }}>
                  Our platform integrates automated coding tests, structured
                  technical interviews, and HR assessment modules, ensuring a
                  holistic evaluation of every candidate.
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src={aboutdis}
              alt="ExiHire AI Hiring Platform"
              sx={{ width: "100%", borderRadius: 4, boxShadow: 3 }}
            />
          </Grid>
        </Grid>
      </Container>

      {/* Values Section */}
      <Box sx={{ bgcolor: "background.default", py: { xs: 1, md: 2 } }}>
        <Container>
          <Typography
            variant="h3"
            align="center"
            sx={{
              fontWeight: "bold",
              color: "#4338CA",
              mb: { xs: 4, md: 6 },
              fontSize: { xs: "2rem", md: "3rem" },
            }}
            gutterBottom
          >
            Our Core Pillars
          </Typography>
          <Grid container spacing={4}>
            {values.map((value, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    textAlign: "center",
                    p: 2,
                    borderRadius: 3,
                    boxShadow: 3,
                  }}
                >
                  <CardContent>
                    <Box sx={{ color: "primary.main", mb: 2 }}>
                      {value.icon}
                    </Box>
                    <Typography variant="h6" gutterBottom>
                      {value.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {value.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Animated FAQ Section */}
      <Box sx={{ mt: { xs: 2, md: 4 }, textAlign: "center", px: 2, pb: 8 }}>
        <Typography
          variant="h4"
          gutterBottom
          align="center"
          sx={{
            fontWeight: "bold",
            color: "#4338CA",
            fontSize: { xs: "1.75rem", md: "2.125rem" },
          }}
        >
          Frequently Asked Questions
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Have questions? We're here to help you choose the right plan.
        </Typography>

        {/* Search Bar */}
        <TextField
          label="Search FAQs..."
          variant="outlined"
          fullWidth
          sx={{ mb: 4, maxWidth: 600, mx: "auto" }}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />

        <Box sx={{ mt: 6, maxWidth: 800, mx: "auto" }}>
          {filteredFaqs.length ? (
            filteredFaqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.4 }}
                style={{ marginBottom: "16px" }}
              >
                <Accordion
                  expanded={expandedFaq === index}
                  onChange={() => handleAccordionChange(index)}
                  sx={{
                    borderRadius: 3,
                    boxShadow: 3,
                    "&:before": { display: "none" },
                    transition: "all 0.3s",
                    "&:hover": { boxShadow: 6 },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <HelpOutline sx={{ mr: 2, color: "primary.main" }} />
                    <Typography fontStyle="italic" fontWeight={500}>
                      {faq.q}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography color="text.secondary">{faq.a}</Typography>
                  </AccordionDetails>
                </Accordion>
              </motion.div>
            ))
          ) : (
            <Typography variant="body1" color="text.secondary">
              No FAQs match your search.
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default About;
